import { useState, useEffect } from 'react'
import {Lock, LogOut, Download, Search, Filter, Trash2, X} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { useSubmissions } from '@/hooks/useSubmissions'

export default function AdminPanel() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [loginData, setLoginData] = useState({ email: '', password: '' })
  const [loginError, setLoginError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  const {
    submissions,
    loading,
    total,
    fetchSubmissions,
    updateStatus,
    deleteSubmission,
    exportToCSV
  } = useSubmissions()

  useEffect(() => {
    // Verificar se já está autenticado
    if (lumi.auth.isAuthenticated && lumi.auth.user?.userRole === 'ADMIN') {
      setIsAuthenticated(true)
    }
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoginError('')
    
    try {
      const { user } = await lumi.auth.signInWithPassword({
        email: loginData.email,
        password: loginData.password
      })

      if (user.userRole !== 'ADMIN') {
        setLoginError('Acesso negado. Apenas administradores podem acessar.')
        await lumi.auth.signOut()
        return
      }

      setIsAuthenticated(true)
      console.log('Admin login successful:', user)
    } catch (error) {
      console.error('Login failed:', error)
      setLoginError('E-mail ou senha incorretos.')
    }
  }

  const handleLogout = async () => {
    await lumi.auth.signOut()
    setIsAuthenticated(false)
    setLoginData({ email: '', password: '' })
  }

  const handleSearch = () => {
    fetchSubmissions({
      search: searchTerm,
      dateFrom,
      dateTo
    })
  }

  const handleClearFilters = () => {
    setSearchTerm('')
    setDateFrom('')
    setDateTo('')
    fetchSubmissions()
  }

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await updateStatus(id, newStatus as any)
    } catch (error) {
      alert('Erro ao atualizar status')
    }
  }

  const handleDelete = async (id: string, nome: string) => {
    if (confirm(`Tem certeza que deseja excluir a candidatura de ${nome}?`)) {
      try {
        await deleteSubmission(id)
      } catch (error) {
        alert('Erro ao excluir candidatura')
      }
    }
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-deep via-violet-medium to-rose-deep/40 flex items-center justify-center px-6">
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 md:p-12 w-full max-w-md border border-violet-dust/30">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-rose-dust/30 mx-auto mb-6">
            <Lock className="w-8 h-8 text-rose-deep" />
          </div>
          
          <h1 className="font-serif text-3xl font-bold text-violet-deep text-center mb-2">
            Painel Administrativo
          </h1>
          <p className="font-sans text-gray-600 text-center mb-8">
            Área restrita - Acesso apenas para administradores
          </p>

          {loginError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-center font-sans text-sm">
              {loginError}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block font-sans text-gray-700 font-medium mb-2">
                E-mail
              </label>
              <input
                type="email"
                required
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-violet-medium focus:outline-none transition-colors font-sans"
                placeholder="admin@example.com"
              />
            </div>

            <div>
              <label className="block font-sans text-gray-700 font-medium mb-2">
                Senha
              </label>
              <input
                type="password"
                required
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-violet-medium focus:outline-none transition-colors font-sans"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              className="group relative w-full bg-gradient-to-r from-violet-deep via-violet-medium to-rose-deep hover:from-rose-deep hover:via-violet-medium hover:to-violet-deep text-white font-sans font-bold text-lg py-4 rounded-full transition-all duration-300 shadow-[0_0_30px_rgba(139,92,246,0.6)] hover:shadow-[0_0_50px_rgba(139,92,246,1)] transform hover:scale-105"
            >
              <span className="relative z-10">Entrar</span>
              <div className="absolute inset-0 bg-white/20 rounded-full blur-lg group-hover:blur-xl transition-all"></div>
            </button>
          </form>

          <p className="font-sans text-xs text-gray-500 text-center mt-6">
            Credenciais de acesso fornecidas pelo sistema
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-deep/10 via-violet-medium/10 to-rose-deep/10">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-sm border-b border-violet-dust/30 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="font-serif text-2xl font-bold text-violet-deep">
              Painel Administrativo
            </h1>
            <p className="font-sans text-sm text-gray-600">
              {total} candidatura{total !== 1 ? 's' : ''} registrada{total !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <a
              href="/admin/leitura"
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-yellow-600 hover:to-amber-600 text-white font-sans font-bold px-6 py-2 rounded-full transition-all duration-300 shadow-lg hover:shadow-[0_0_20px_rgba(200,146,58,0.6)] transform hover:scale-105"
            >
              Admin — Leitura do Campo Vibracional
            </a>
            <a
              href="/admin/leitura-inscricao"
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-amber-700 to-yellow-700 hover:from-yellow-700 hover:to-amber-700 text-white font-sans font-bold px-6 py-2 rounded-full transition-all duration-300 shadow-lg hover:shadow-[0_0_20px_rgba(200,146,58,0.6)] transform hover:scale-105"
            >
              Admin — Inscrições da Leitura
            </a>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-sans font-bold px-6 py-2 rounded-full transition-all duration-300 shadow-lg hover:shadow-[0_0_20px_rgba(239,68,68,0.6)] transform hover:scale-105"
          >
            <LogOut className="w-4 h-4" />
            Sair
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters and Actions */}
        <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-sm p-6 mb-6 border border-violet-dust/20">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nome, email ou WhatsApp..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-violet-medium focus:outline-none font-sans text-sm"
                />
              </div>
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-gray-200 rounded-lg hover:border-violet-medium transition-colors font-sans text-sm"
              >
                <Filter className="w-4 h-4" />
                Filtros
              </button>
              <button
                onClick={exportToCSV}
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-violet-deep to-violet-medium hover:from-violet-medium hover:to-rose-deep text-white font-sans font-bold px-6 py-2 rounded-full transition-all duration-300 shadow-lg hover:shadow-[0_0_25px_rgba(139,92,246,0.6)] transform hover:scale-105"
              >
                <Download className="w-5 h-5" />
                Exportar CSV
              </button>
            </div>

            {showFilters && (
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
                <div className="flex-1">
                  <label className="block font-sans text-xs text-gray-600 mb-1">Data Inicial</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-violet-medium focus:outline-none font-sans text-sm"
                  />
                </div>
                <div className="flex-1">
                  <label className="block font-sans text-xs text-gray-600 mb-1">Data Final</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-violet-medium focus:outline-none font-sans text-sm"
                  />
                </div>
                <div className="flex gap-2 items-end">
                  <button
                    onClick={handleSearch}
                    className="px-6 py-2 bg-violet-deep text-white rounded-lg hover:bg-violet-medium transition-colors font-sans text-sm font-medium"
                  >
                    Aplicar
                  </button>
                  <button
                    onClick={handleClearFilters}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-sans text-sm"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Candidates List */}
        {loading ? (
          <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-sm p-12 text-center border border-violet-dust/20">
            <div className="animate-spin w-8 h-8 border-4 border-violet-deep border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="font-sans text-gray-500">Carregando candidaturas...</p>
          </div>
        ) : submissions.length === 0 ? (
          <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-sm p-12 text-center border border-violet-dust/20">
            <p className="font-sans text-gray-500">
              Nenhuma candidatura encontrada
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {submissions.map((candidate) => (
              <div key={candidate._id} className="bg-white/95 backdrop-blur-sm rounded-xl shadow-sm p-6 border border-violet-dust/20 hover:shadow-lg transition-shadow">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                  <div className="flex-1 space-y-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-serif text-xl font-bold text-violet-deep mb-1">
                          {candidate.nome}
                        </h3>
                        <p className="font-sans text-sm text-gray-600">
                          {new Date(candidate.createdAt).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDelete(candidate._id, candidate.nome)}
                        className="text-red-500 hover:text-red-700 transition-colors p-2"
                        title="Excluir candidatura"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <p className="font-sans text-xs text-gray-500 mb-1">E-mail</p>
                        <p className="font-sans text-sm text-gray-700">{candidate.email}</p>
                      </div>
                      <div>
                        <p className="font-sans text-xs text-gray-500 mb-1">WhatsApp</p>
                        <p className="font-sans text-sm text-gray-700">{candidate.whatsapp}</p>
                      </div>
                      <div>
                        <p className="font-sans text-xs text-gray-500 mb-1">Data de Nascimento</p>
                        <p className="font-sans text-sm text-gray-700">{candidate.dataNascimento}</p>
                      </div>
                      <div>
                        <p className="font-sans text-xs text-gray-500 mb-1">Profissão</p>
                        <p className="font-sans text-sm text-gray-700">{candidate.profissao}</p>
                      </div>
                      <div>
                        <p className="font-sans text-xs text-gray-500 mb-1">Cidade e Estado</p>
                        <p className="font-sans text-sm text-gray-700">{candidate.cidadeEstado}</p>
                      </div>
                    </div>

                    <div className="border-t border-gray-100 pt-4 space-y-3">
                      <div>
                        <p className="font-sans text-xs text-gray-500 mb-1">1. Áreas travadas</p>
                        <p className="font-sans text-sm text-gray-700">{candidate.areasTravadas}</p>
                      </div>
                      <div>
                        <p className="font-sans text-xs text-gray-500 mb-1">2. Padrão/ciclo que se repete</p>
                        <p className="font-sans text-sm text-gray-700 whitespace-pre-wrap">{candidate.padraoRepetitivo}</p>
                      </div>
                      <div>
                        <p className="font-sans text-xs text-gray-500 mb-1">3. Tentativas de resolução</p>
                        <p className="font-sans text-sm text-gray-700 whitespace-pre-wrap">{candidate.tentativasResolucao}</p>
                      </div>
                      <div>
                        <p className="font-sans text-xs text-gray-500 mb-1">4. Nível de comprometimento (0-10)</p>
                        <p className="font-sans text-sm text-gray-700">{candidate.nivelComprometimento}</p>
                      </div>
                      <div>
                        <p className="font-sans text-xs text-gray-500 mb-1">5. Disponibilidade para investir</p>
                        <p className="font-sans text-sm text-gray-700">{candidate.disponibilidadeInvestir}</p>
                      </div>
                      <div>
                        <p className="font-sans text-xs text-gray-500 mb-1">6. Uma coisa que mudaria</p>
                        <p className="font-sans text-sm text-gray-700 whitespace-pre-wrap">{candidate.umaCoisa}</p>
                      </div>
                      <div>
                        <p className="font-sans text-xs text-gray-500 mb-1">7. Motivação para preencher agora</p>
                        <p className="font-sans text-sm text-gray-700 whitespace-pre-wrap">{candidate.motivacaoAgora}</p>
                      </div>
                    </div>
                  </div>

                  <div className="lg:w-64 space-y-3">
                    <div>
                      <label className="block font-sans text-xs text-gray-500 mb-2">Status</label>
                      <select 
                        value={candidate.status}
                        onChange={(e) => handleStatusChange(candidate._id, e.target.value)}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-violet-medium focus:outline-none font-sans text-sm"
                      >
                        <option>Pendente</option>
                        <option>Contato Realizado</option>
                        <option>Aprovada para Conversa</option>
                        <option>Não Aprovada</option>
                      </select>
                    </div>
                    <div className={`px-4 py-2 rounded-lg text-center font-sans text-sm font-medium ${
                      candidate.status === 'Aprovada para Conversa' ? 'bg-green-100 text-green-700' :
                      candidate.status === 'Contato Realizado' ? 'bg-blue-100 text-blue-700' :
                      candidate.status === 'Não Aprovada' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {candidate.status}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
